// SIWS CUSTOM: B2B size matrix for the Cornwell Metal Tee.
// Reads variant map from data-variants, renders a qty input per size,
// shows live running total + volume tier indicator, submits all sizes
// in a single /cart/add.js call (items array) then redirects to /cart.

(function () {
  // SIWS CUSTOM: Size display order — must match variant titles in Shopify exactly.
  // Upcharge per size: 2XL +$2, 3XL/4XL/5XL +$4. XL and below have no upcharge.
  const SIWS_TEE_SIZE_ORDER = ['S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];
  const SIWS_TEE_UPCHARGE   = { '2XL': '+$2', '3XL': '+$4', '4XL': '+$4', '5XL': '+$4' };

  // SIWS CUSTOM: Volume tier thresholds.
  const SIWS_TEE_TIERS = [
    { min: 100, discount: 20, msg: () => '✅ 20% off unlocked — maximum discount!' },
    { min: 72,  discount: 15, msg: (n) => `✅ 15% off unlocked — add ${100 - n} more for 20%` },
    { min: 48,  discount: 10, msg: (n) => `✅ 10% off unlocked — add ${72 - n} more for 15%` },
    { min: 0,   discount: 0,  msg: (n) => `Add ${48 - n} more to unlock 10% off` },
  ];

  // SIWS CUSTOM: Phase 1 metafield-driven config — read JSON from a data-* attribute,
  // return the parsed value when present and well-formed, otherwise return fallback.
  // null / empty array → fallback. {} is preserved (legitimate "no upcharges anywhere").
  function siwsReadJsonDataAttr(el, attr, fallback) {
    const raw = el.dataset[attr];
    if (!raw) return fallback;
    try {
      const parsed = JSON.parse(raw);
      if (parsed == null) return fallback;
      if (Array.isArray(parsed) && parsed.length === 0) return fallback;
      return parsed;
    } catch (e) {
      console.warn('SIWS: failed to parse data-' + attr + ', using fallback', e);
      return fallback;
    }
  }

  // SIWS CUSTOM: Phase 1 metafield-driven config — convert metafield-shaped tier
  // array ([{qty,discount},...]) into the {min, msg(n)} shape the rest of the
  // code already uses. Sorts internally so admin order doesn't matter.
  function siwsBuildTiersFromMetafield(rawTiers) {
    const asc  = [...rawTiers].sort((a, b) => a.qty - b.qty);
    const desc = [...asc].reverse();
    const built = desc.map(function (t, i) {
      const higher = desc[i - 1];
      return {
        min: t.qty,
        discount: t.discount,
        msg: higher
          ? (n) => `✅ ${t.discount}% off unlocked — add ${higher.qty - n} more for ${higher.discount}%`
          : ()  => `✅ ${t.discount}% off unlocked — maximum discount!`,
      };
    });
    built.push({
      min: 0,
      discount: 0,
      msg: (n) => `Add ${asc[0].qty - n} more to unlock ${asc[0].discount}% off`,
    });
    return built;
  }

  function siwsTeeGetTierMsg(total, tiersList) {
    for (const tier of tiersList) {
      if (total >= tier.min) return tier.msg(total);
    }
    return '';
  }

  // SIWS CUSTOM: Phase 3 — find the unlocked tier (highest non-zero min ≤ total).
  function siwsTeeGetUnlockedTier(total, tiersList) {
    for (const tier of tiersList) {
      if (tier.min > 0 && total >= tier.min) return tier;
    }
    return null;
  }

  // SIWS CUSTOM: Phase 3 — find the next tier (lowest min strictly > total), or null at top.
  function siwsTeeGetNextTier(total, tiersList) {
    const upcoming = tiersList
      .filter(t => t.min > total)
      .sort((a, b) => a.min - b.min);
    return upcoming[0] || null;
  }

  // SIWS CUSTOM: Phase 3 — format integer cents as $X.XX.
  function siwsFormatCents(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // SIWS CUSTOM: Phase 3 — pluralized size label for breakdown rows
  // ("5 mediums, 10 larges, 2 XLs"). Falls through for 2XL/3XL etc → "+s".
  const SIWS_TEE_SIZE_LABELS = {
    S:  { singular: 'Small',  plural: 'Smalls'  },
    M:  { singular: 'Medium', plural: 'Mediums' },
    L:  { singular: 'Large',  plural: 'Larges'  },
    XL: { singular: 'XL',     plural: 'XLs'     },
  };

  function siwsTeeSizeLabel(size, qty) {
    const map = SIWS_TEE_SIZE_LABELS[size];
    if (map) return qty === 1 ? map.singular : map.plural;
    return qty === 1 ? size : size + 's';
  }

  // SIWS CUSTOM: Phase 3.2 — next-tier callout pulled out of the table footer.
  // Returns "Add X more for Y%" + dollar-anchored secondary copy + horizontal progress bar.
  // Max-tier branch (no `next`) returns a celebratory variant with no progress bar.
  // The deltaSavings figure uses the order's average per-unit price (subtotalCents / total)
  // projected to next.min units — accurate for size-mixed carts without fixing on a single SKU.
  function siwsTeeBuildCalloutHtml(unlocked, next, combinedQty, subtotalCents, discountCents, cartPinUpQty, maxTierMin) {
    if (!next) {
      return (
        `<div class="siws-tee-summary__callout siws-tee-summary__callout--max">` +
          `<span class="siws-tee-summary__callout-icon">✅</span>` +
          `<div class="siws-tee-summary__callout-text">` +
            `<p class="siws-tee-summary__callout-primary">Maximum Tier Reached</p>` +
            `<p class="siws-tee-summary__callout-secondary">Saved ${siwsFormatCents(discountCents)} on this order</p>` +
          `</div>` +
        `</div>`
      );
    }

    // SIWS CUSTOM: Phase 2.5 — bar is cumulative-to-max, not tier-relative. Tier crossings
    // become language events instead of visual cliffs. Label denominator matches the bar.
    const progressPct = Math.min((combinedQty / maxTierMin) * 100, 100);
    const remaining   = next.min - combinedQty;

    // SIWS CUSTOM: Phase 4 — cart context line; only renders when cartPinUpQty > 0.
    const cartContextLine = cartPinUpQty > 0
      ? `<p class="siws-tee-summary__callout-cart-context">${cartPinUpQty} already in cart from other Pin-Up garments</p>`
      : '';

    return (
      `<div class="siws-tee-summary__callout">` +
        `<span class="siws-tee-summary__callout-icon">↑</span>` +
        `<div class="siws-tee-summary__callout-text">` +
          `<p class="siws-tee-summary__callout-primary">Add ${remaining} more for ${next.discount}% off</p>` +
          cartContextLine +
          `<div class="siws-tee-summary__callout-progress" ` +
               `style="display:block;width:100%;height:14px;` +
                      `background:#d1d5db;border-radius:7px;` +
                      `overflow:hidden;margin-top:8px;">` +
            `<div class="siws-tee-summary__callout-progress-bar" ` +
                 `style="display:block;height:14px;` +
                        `background:#16a34a;` +
                        `width:${progressPct.toFixed(1)}%;"></div>` +
          `</div>` +
          `<p class="siws-tee-summary__callout-progress-label">${combinedQty} / ${maxTierMin}</p>` +
        `</div>` +
      `</div>`
    );
  }

  // SIWS CUSTOM: Phase 4 — fetch /cart.js and sum quantities for line items whose
  // product_id is in the supplied list. Returns Promise<integer>. Any error path
  // (network fail, non-OK response, JSON parse fail) resolves to 0 — graceful degrade.
  // Mirrors the pattern in cart.js:siwsUpdateVolumeMsg but filters by product_id, not title.
  function siwsTeeFetchPinUpCartQty(idsList) {
    if (!idsList || !idsList.length) return Promise.resolve(0);
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : { items: [] }; })
      .then(function (cart) {
        return (cart.items || [])
          .filter(function (item) { return idsList.indexOf(item.product_id) !== -1; })
          .reduce(function (sum, item) { return sum + item.quantity; }, 0);
      })
      .catch(function () { return 0; });
  }

  document.addEventListener('DOMContentLoaded', function () {
    const matrix = document.getElementById('siws-tee-matrix');
    if (!matrix) return;

    // SIWS CUSTOM: Parse variant map emitted by Liquid — { "S": 123456, "M": 123457, ... }
    let variantMap;
    try {
      variantMap = JSON.parse(matrix.dataset.variants);
    } catch (e) {
      console.error('SIWS: Failed to parse tee variant map', e);
      return;
    }

    // SIWS CUSTOM: Phase 1 — resolve size order / upcharges / tiers from metafield
    // data attributes when present, else fall back to the constants above.
    const sizeOrder = siwsReadJsonDataAttr(matrix, 'sizeOrder',     SIWS_TEE_SIZE_ORDER);
    const upcharges = siwsReadJsonDataAttr(matrix, 'sizeUpcharges', SIWS_TEE_UPCHARGE);
    const tiersRaw  = siwsReadJsonDataAttr(matrix, 'tiers',         null);
    const tiers     = (tiersRaw && Array.isArray(tiersRaw) && tiersRaw.length > 0)
      ? siwsBuildTiersFromMetafield(tiersRaw)
      : SIWS_TEE_TIERS;

    // SIWS CUSTOM: Phase 3 — variant prices (cents) drive per-line cost rows + subtotal.
    // Null = graceful degrade (defensive; variant.price always emits in practice).
    const variantPrices = siwsReadJsonDataAttr(matrix, 'variantPrices', null);

    // SIWS CUSTOM: Phase 4 — cross-garment cart-aware tier math. pinUpIds is null
    // when the data attribute is absent (No One tee or any non-collection product).
    // cartPinUpQty starts at 0 and is updated by the initial cart fetch + cart-update subscription.
    const pinUpIds     = siwsReadJsonDataAttr(matrix, 'pinUpProductIds', null);
    let   cartPinUpQty = 0;

    const grid       = document.getElementById('siws-tee-grid');
    const summaryEl  = document.getElementById('siws-tee-summary');
    const errorEl    = document.getElementById('siws-tee-error');
    const atcBtn     = document.getElementById('siws-tee-atc-btn');
    // SIWS CUSTOM: Phase 3 — cross-garment disclaimer is a SIBLING of summaryEl,
    // currently NOT rendered by Liquid (deferred pending M&C cross-garment-discount confirmation).
    // Reference resolves to null today; render path guards via `if (crossNoteEl)` so the
    // JS is ready to light up the disclaimer when Liquid re-emits the element.
    const crossNoteEl = document.getElementById('siws-tee-cross-garment-note');

    // SIWS CUSTOM: Render one input cell per size (in defined order, skip if not in variant map).
    sizeOrder.forEach(function (size) {
      if (!variantMap[size]) return;

      const cell  = document.createElement('div');
      cell.className = 'siws-tee-matrix__cell';

      const upcharge = upcharges[size] || null;
      const label = document.createElement('label');
      label.className = 'siws-tee-matrix__label';
      label.htmlFor   = 'siws-tee-qty-' + size;
      // SIWS CUSTOM: Always render <small> — hidden for no-upcharge sizes so all labels
      // are the same height and all input boxes align on the same baseline.
      label.innerHTML = size + (upcharge
        ? '<small>' + upcharge + '</small>'
        : '<small style="visibility:hidden" aria-hidden="true">+$0</small>');

      const input = document.createElement('input');
      input.type        = 'number';
      input.id          = 'siws-tee-qty-' + size;
      input.className   = 'siws-tee-matrix__input';
      input.min         = '0';
      input.value       = '';
      input.placeholder = '0';
      input.dataset.size      = size;
      input.dataset.variantId = variantMap[size];
      input.setAttribute('inputmode', 'numeric');

      input.addEventListener('input',  siwsTeeUpdateSummary);
      input.addEventListener('change', siwsTeeUpdateSummary);
      input.addEventListener('blur', function () {
        if (input.value === '') input.placeholder = '0';
      });

      cell.appendChild(label);
      cell.appendChild(input);
      grid.appendChild(cell);
    });

    // SIWS CUSTOM: Phase 3 — render full order-summary breakdown.
    // Empty state: heading + floor hint. Populated: per-size table + tier badge +
    // estimated discount + estimated total + (if crossNoteEl present) sibling disclaimer.
    // innerHTML replace pattern matches card-holder-picker.js.
    function siwsTeeUpdateSummary() {
      const pageQty     = siwsTeeGetTotal();
      const combinedQty = pageQty + cartPinUpQty;
      errorEl.style.display = 'none';
      if (crossNoteEl) crossNoteEl.hidden = true;

      // Empty state — gated on pageQty (no point showing summary if user typed nothing here)
      if (pageQty === 0) {
        summaryEl.innerHTML =
          `<h4 class="screwdriver-summary__title" ` +
              `style="font-size:1.6rem;font-weight:700;` +
                     `color:rgba(var(--color-foreground),1);` +
                     `text-transform:none;letter-spacing:normal;` +
                     `margin:0 0 1rem;">Adding to Cart</h4>` +
          `<p class="siws-tee-summary__hint">${siwsTeeGetTierMsg(0, tiers)}</p>`;
        return;
      }

      // Graceful degrade — variant prices missing
      if (!variantPrices) {
        summaryEl.innerHTML =
          `<h4 class="screwdriver-summary__title">Adding to Cart</h4>` +
          `<p class="siws-tee-summary__hint">Total: ${pageQty} ${pageQty === 1 ? 'shirt' : 'shirts'}</p>` +
          `<p class="siws-tee-summary__hint">${siwsTeeGetTierMsg(combinedQty, tiers)}</p>`;
        return;
      }

      // Populated state — tier math uses combinedQty, dollar math uses pageQty × prices
      const rows          = siwsTeeBuildSizeRows();
      const subtotalCents = siwsTeeComputeSubtotal();
      const unlocked      = siwsTeeGetUnlockedTier(combinedQty, tiers);
      const next          = siwsTeeGetNextTier(combinedQty, tiers);

      const discountPct   = unlocked ? unlocked.discount : 0;
      const discountCents = Math.round(subtotalCents * discountPct / 100);
      const finalCents    = subtotalCents - discountCents;

      const tierBadgeText = unlocked
        ? `✅ ${unlocked.discount}% off — ${unlocked.min}+ tier`
        : 'No tier unlocked yet';
      const discountRow = unlocked
        ? `<tr><td>Estimated discount</td><td class="screwdriver-summary__qty-cell">−${siwsFormatCents(discountCents)}</td></tr>`
        : '';

      // SIWS CUSTOM: Phase 2.5 — top of the ladder for cumulative progress-bar math.
      // tiers[] is sorted descending by min in both constants and metafield-built variants,
      // with the min:0 sentinel at the end → tiers[0].min is the max-tier threshold.
      const maxTierMin = tiers[0].min;

      summaryEl.innerHTML =
        `<h4 class="screwdriver-summary__title" ` +
            `style="font-size:1.6rem;font-weight:700;` +
                   `color:rgba(var(--color-foreground),1);` +
                   `text-transform:none;letter-spacing:normal;` +
                   `margin:0 0 1rem;">Adding to Cart</h4>` +
        `<table class="screwdriver-summary__table">` +
          `<tbody>${rows}</tbody>` +
          `<tfoot>` +
            `<tr><td>Subtotal</td><td class="screwdriver-summary__qty-cell">${pageQty} ${pageQty === 1 ? 'shirt' : 'shirts'} · ${siwsFormatCents(subtotalCents)}</td></tr>` +
            `<tr><td>Discount tier</td><td class="screwdriver-summary__qty-cell">${tierBadgeText}</td></tr>` +
            discountRow +
            `<tr class="screwdriver-summary__total-row"><td>Estimated total</td><td class="screwdriver-summary__qty-cell">${siwsFormatCents(finalCents)}</td></tr>` +
          `</tfoot>` +
        `</table>` +
        siwsTeeBuildCalloutHtml(unlocked, next, combinedQty, subtotalCents, discountCents, cartPinUpQty, maxTierMin);

      if (crossNoteEl) crossNoteEl.hidden = false;
    }

    // SIWS CUSTOM: Phase 3 — build per-size <tr> rows for inputs with qty > 0,
    // in sizeOrder. Each row: "{qty} {label}" cell + "{qty} × $price = $line" cell.
    function siwsTeeBuildSizeRows() {
      const out = [];
      sizeOrder.forEach(function (size) {
        const inp = grid.querySelector(`[data-size="${size}"]`);
        if (!inp) return;
        const qty = parseInt(inp.value, 10);
        if (!(qty > 0)) return;

        const priceCents = variantPrices[size];
        const lineCents  = priceCents != null ? priceCents * qty : null;
        const label      = siwsTeeSizeLabel(size, qty);

        const costCell = lineCents != null
          ? `${qty} × ${siwsFormatCents(priceCents)} = ${siwsFormatCents(lineCents)}`
          : `${qty} units`;

        out.push(
          `<tr><td>(${qty}) ${label}</td>` +
          `<td class="screwdriver-summary__qty-cell">${costCell}</td></tr>`
        );
      });
      return out.join('');
    }

    // SIWS CUSTOM: Phase 3 — sum (qty × price_cents) across all inputs with qty > 0.
    // Caller must guard !variantPrices before invoking (this assumes it's defined).
    function siwsTeeComputeSubtotal() {
      let subtotal = 0;
      grid.querySelectorAll('.siws-tee-matrix__input').forEach(function (inp) {
        const qty = parseInt(inp.value, 10);
        if (!(qty > 0)) return;
        const size = inp.dataset.size;
        const priceCents = variantPrices[size];
        if (priceCents != null) subtotal += priceCents * qty;
      });
      return subtotal;
    }

    function siwsTeeGetTotal() {
      let total = 0;
      grid.querySelectorAll('.siws-tee-matrix__input').forEach(function (inp) {
        const v = parseInt(inp.value, 10);
        if (v > 0) total += v;
      });
      return total;
    }

    // SIWS CUSTOM: Add to Cart — collect all sizes with qty > 0, POST to /cart/add.js.
    atcBtn.addEventListener('click', function () {
      const items = [];
      grid.querySelectorAll('.siws-tee-matrix__input').forEach(function (inp) {
        const qty = parseInt(inp.value, 10);
        if (qty > 0) {
          items.push({ id: parseInt(inp.dataset.variantId, 10), quantity: qty });
        }
      });

      if (items.length === 0) {
        errorEl.style.display = 'block';
        return;
      }

      atcBtn.disabled    = true;
      atcBtn.textContent = 'Adding…';
      errorEl.style.display = 'none';

      // SIWS CUSTOM (2026-05-12): augment body with Section Rendering API hints
      // so the Dawn cart drawer/notification can re-render itself from the response.
      var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      var body = {
        items: items,
        sections:     cart ? cart.getSectionsToRender().map(function (s) { return s.id; }) : ['cart-icon-bubble'],
        sections_url: window.location.pathname,
      };

      fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      })
        .then(async function (res) {
          if (!res.ok) throw new Error('Cart add failed');
          // SIWS CUSTOM (2026-05-12): swap redirect for Dawn cart drawer/notification.
          var responseData = await res.json();
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'siws-tee', cartData: responseData });
          }
          if (cart) {
            cart.renderContents(responseData);
          } else {
            // SIWS CUSTOM (2026-05-12): Page-mode — stay on PDP, refresh cart bubble.
            var newBubbleHTML = responseData.sections && responseData.sections['cart-icon-bubble'];
            var liveBubble = document.getElementById('cart-icon-bubble');
            if (newBubbleHTML && liveBubble) {
              var parsed = new DOMParser().parseFromString(newBubbleHTML, 'text/html');
              var inner = parsed.querySelector('.shopify-section');
              if (inner) liveBubble.innerHTML = inner.innerHTML;
            }
            // SIWS CUSTOM (2026-05-12): clear picker state so a re-click can't double-add.
            grid.querySelectorAll('.siws-tee-matrix__input').forEach(function (inp) { inp.value = ''; });
            siwsTeeUpdateSummary();
            atcBtn.disabled    = false;
            atcBtn.textContent = 'Add to Cart';
          }
        })
        .catch(function () {
          atcBtn.disabled    = false;
          atcBtn.textContent = 'Add to Cart';
          errorEl.textContent = 'Something went wrong — please try again.';
          errorEl.style.display = 'block';
        });
    });

    // Run once on load to initialise tier message.
    siwsTeeUpdateSummary();

    // SIWS CUSTOM: Phase 4 — cross-garment cart-aware tier math. Skip entirely
    // when pinUpIds is null/empty (No One tee and other non-collection products).
    if (pinUpIds && pinUpIds.length) {
      // Initial fetch — refresh summary once cart qty resolves.
      siwsTeeFetchPinUpCartQty(pinUpIds).then(function (qty) {
        cartPinUpQty = qty;
        siwsTeeUpdateSummary();
      });
      // Re-fetch + re-render on every cart-update event. Defensive typeof guard
      // mirrors cart.js:802 — pubsub.js may not have loaded by this script's evaluation.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
          siwsTeeFetchPinUpCartQty(pinUpIds).then(function (qty) {
            cartPinUpQty = qty;
            siwsTeeUpdateSummary();
          });
        });
      }
    }
  });
})();
